import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link, useSearchParams } from "react-router-dom";
import CountrySelect from "../components/CountrySelect.jsx";
import CustomSelect from "../components/CustomSelect.jsx";
import LeaderboardTable from "../components/LeaderboardTable.jsx";
import { useAuth } from "../hooks/useAuth.js";
import { COUNTRY_NAMES, FlagIcon, countryName, stateName } from "../utils/countries.jsx";

const API_BASE = import.meta.env.VITE_API_BASE_URL || "/v2/api";

const WINDOWS = [
  { value: "alltime", label: "All time" },
  { value: "30d", label: "30 days" },
  { value: "7d", label: "7 days" },
];

const VALID_WINDOWS = new Set(["alltime", "30d", "7d"]);

const LeaderboardPage = () => {
  const { user, authFetch } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  // Derive filters directly from the URL — searchParams is the single source of
  // truth. This means external navigation (back/forward, in-page links) is always
  // reflected without a separate layer of state fighting the URL.
  const rawWindow = searchParams.get("window");
  const window_ = VALID_WINDOWS.has(rawWindow) ? rawWindow : "alltime";
  const country = searchParams.get("country") || "";
  const state = country === "US" ? (searchParams.get("state") || "") : "";

  const [entries, setEntries] = useState([]);
  const [pagination, setPagination] = useState({ count: 0, next: null, previous: null });
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);

  const [myRank, setMyRank] = useState(null);
  const [myRankLoading, setMyRankLoading] = useState(false);

  // Available filter values driven by the backend. Populated lazily — empty arrays
  // while the first request is in flight, which collapses the dropdowns to
  // "Global only" until the server responds.
  const [availableCountries, setAvailableCountries] = useState([]);
  const [availableStates, setAvailableStates] = useState([]);

  // Tracks whether at least one successful /filters/ response has been
  // received. Used in the error handler to decide between "fall back to
  // static list" (never loaded) and "preserve current options" (transient
  // mid-session failure). A ref avoids adding array state to useCallback
  // deps, which would cause an infinite refetch loop.
  const filtersLoadedRef = useRef(false);

  // Reset to page 1 when filters change
  const prevFilters = useRef({ window_, country, state });
  useEffect(() => {
    const prev = prevFilters.current;
    if (prev.window_ !== window_ || prev.country !== country || prev.state !== state) {
      setPage(1);
      prevFilters.current = { window_, country, state };
    }
  }, [window_, country, state]);

  // Helpers that update the URL (setSearchParams replaces local state setters)
  const setWindow = (w) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    if (w === "alltime") p.delete("window"); else p.set("window", w);
    return p;
  }, { replace: true });

  const setCountry = (c) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    if (c) p.set("country", c); else p.delete("country");
    p.delete("state"); // clear state whenever country changes
    return p;
  }, { replace: true });

  const setState = (s) => setSearchParams((prev) => {
    const p = new URLSearchParams(prev);
    if (s) p.set("state", s); else p.delete("state");
    return p;
  }, { replace: true });

  // Fetch available filter options from the backend. Refetched whenever the
  // country filter flips US ↔ non-US so the state sub-list stays scoped to
  // countries that actually have US users.
  const fetchFilters = useCallback(async (signal) => {
    try {
      const params = new URLSearchParams();
      if (country === "US") params.set("country", "US");
      const url = params.toString()
        ? `${API_BASE}/auth/leaderboard/filters/?${params}`
        : `${API_BASE}/auth/leaderboard/filters/`;
      const r = await fetch(url, { signal });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setAvailableCountries(Array.isArray(data.countries) ? data.countries : []);
      setAvailableStates(Array.isArray(data.states) ? data.states : []);
      filtersLoadedRef.current = true;
    } catch (err) {
      if (err.name === "AbortError") return;
      if (!filtersLoadedRef.current) {
        // Initial load failed — fall back to the full static country list so
        // users can still reach country/state leaderboards even when /filters/
        // is temporarily unhealthy. Matches the previous hard-coded behaviour.
        setAvailableCountries(
          Object.keys(COUNTRY_NAMES).sort((a, b) =>
            countryName(a).localeCompare(countryName(b)),
          ),
        );
      }
      // If we already have options from a prior successful fetch, preserve
      // them so a transient mid-session error doesn't collapse the dropdown.
    }
  }, [country]);

  const fetchLeaderboard = useCallback(async (signal) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ window: window_, page });
      if (country) params.set("country", country);
      if (state) params.set("state", state);
      const r = await fetch(`${API_BASE}/auth/leaderboard/?${params}`, { signal });
      if (!r.ok) throw new Error();
      const data = await r.json();
      setEntries(data.results);
      setPagination({ count: data.count, next: data.next, previous: data.previous });
    } catch (err) {
      if (err.name === "AbortError") return; // stale request cancelled — don't touch state
      setEntries([]);
      setPagination({ count: 0, next: null, previous: null });
    } finally {
      // finally always runs even when catch returns — only clear loading if this
      // request wasn't aborted, so a cancelled stale request doesn't clear the
      // loading state owned by the newer in-flight request.
      if (!signal.aborted) setLoading(false);
    }
  }, [window_, country, state, page]);

  const fetchMyRank = useCallback(async (signal) => {
    if (!user) return;
    setMyRankLoading(true);
    try {
      const params = new URLSearchParams({ window: window_ });
      if (country) params.set("country", country);
      if (state) params.set("state", state);
      const r = await authFetch(`${API_BASE}/auth/leaderboard/me/?${params}`, { signal });
      if (!r.ok) throw new Error();
      setMyRank(await r.json());
    } catch (err) {
      if (err.name === "AbortError") return;
      setMyRank(null);
    } finally {
      if (!signal.aborted) setMyRankLoading(false);
    }
  }, [user, window_, country, state, authFetch]);

  useEffect(() => {
    const controller = new AbortController();
    fetchFilters(controller.signal);
    return () => controller.abort();
  }, [fetchFilters]);
  useEffect(() => {
    const controller = new AbortController();
    fetchLeaderboard(controller.signal);
    return () => controller.abort();
  }, [fetchLeaderboard]);
  useEffect(() => {
    const controller = new AbortController();
    fetchMyRank(controller.signal);
    return () => controller.abort();
  }, [fetchMyRank]);

  const totalPages = Math.ceil(pagination.count / 25);

  // Build the option list for the country dropdown. If the URL has a country
  // selected that the backend no longer reports (e.g. last user from that
  // country was deleted while viewing the page), we still include it so the
  // <select> has a matching option — otherwise React warns and the value
  // silently resets to "".
  const countryOptions = useMemo(() => {
    const codes = new Set(availableCountries);
    if (country) codes.add(country);
    return Array.from(codes).sort((a, b) =>
      countryName(a).localeCompare(countryName(b)),
    );
  }, [availableCountries, country]);

  const stateOptions = useMemo(() => {
    const codes = new Set(availableStates);
    if (state) codes.add(state);
    return Array.from(codes).sort((a, b) =>
      stateName(a).localeCompare(stateName(b)),
    );
  }, [availableStates, state]);

  // Prominent scope label shown under the "Leaderboard" header.
  const scopeTitle = state
    ? `${stateName(state)}, ${countryName(country)}`
    : country
    ? countryName(country)
    : "Global";

  return (
    <div className="space-y-6">
      <Helmet>
        <title>Leaderboard — Top Players | QuizFreak</title>
        <meta name="description" content="See who's leading the pack on QuizFreak. Compete in fun quizzes and climb the global leaderboard." />
        <link rel="canonical" href="https://quizfreak.com/v2/leaderboard/" />
      </Helmet>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-semibold text-white">Leaderboard</h1>
          <div className="mt-2 flex items-center gap-2">
            {country
              ? <FlagIcon code={country} className="text-2xl" />
              : <span className="text-2xl leading-none" aria-hidden="true">🌍</span>
            }
            <span className="text-xl font-semibold text-white">{scopeTitle}</span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2">
          {/* Window */}
          <div className="flex rounded-full border border-white/10 bg-white/5 p-0.5">
            {WINDOWS.map((w) => (
              <button
                key={w.value}
                type="button"
                onClick={() => setWindow(w.value)}
                className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                  window_ === w.value
                    ? "bg-brand-glow text-white"
                    : "text-white/50 hover:text-white"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>

          {/* Country */}
          <CountrySelect
            value={country}
            onChange={setCountry}
            options={countryOptions.map((code) => ({ value: code, label: countryName(code) }))}
          />

          {/* State — only when US selected */}
          {country === "US" && stateOptions.length > 0 && (
            <CustomSelect
              value={state}
              onChange={setState}
              options={stateOptions.map((code) => ({ value: code, label: stateName(code) }))}
              placeholder="All states"
              variant="pill"
            />
          )}
        </div>
      </div>

      {/* My rank card */}
      {user && (
        <div className="rounded-2xl border border-white/10 bg-white/5 px-5 py-4">
          {myRankLoading ? (
            <div className="h-5 w-48 animate-pulse rounded bg-white/10" />
          ) : myRank ? (
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-xs uppercase tracking-widest text-white/40">Your rank</p>
                <p className="mt-1 text-2xl font-bold text-white">
                  {myRank.rank == null ? "Unranked" : `#${myRank.rank}`}
                </p>
                {myRank.percentile != null && (
                  <p className="text-xs text-white/50">Better than {myRank.percentile}% of players</p>
                )}
              </div>
              <div className="text-right">
                <p className="text-xs uppercase tracking-widest text-white/40">
                  {window_ === "alltime" ? "Total pts" : `Pts (${window_})`}
                </p>
                <p className="mt-1 text-2xl font-bold text-brand-glow">
                  {myRank.points?.toLocaleString() ?? 0}
                </p>
              </div>
              {!myRank.scope?.country && user.country && (
                <Link
                  to={`/leaderboard?country=${user.country}${user.state ? `&state=${user.state}` : ""}`}
                  className="text-xs text-brand-glow hover:underline"
                >
                  View {user.state ? `${user.state}, ${user.country}` : user.country} board →
                </Link>
              )}
            </div>
          ) : null}
        </div>
      )}

      {/* Main table */}
      <div className="overflow-hidden rounded-3xl border border-white/10 bg-slate-900/80">
        {loading ? (
          <div className="space-y-px p-2">
            {Array.from({ length: 10 }).map((_, i) => (
              <div key={i} className="h-14 animate-pulse rounded-xl bg-white/5" />
            ))}
          </div>
        ) : (
          <LeaderboardTable entries={entries} highlightUserId={user?.id} />
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={() => setPage((p) => p - 1)}
            disabled={!pagination.previous}
            className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white/60 disabled:opacity-30"
          >
            ← Prev
          </button>
          <span className="text-xs text-white/40">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setPage((p) => p + 1)}
            disabled={!pagination.next}
            className="rounded-full border border-white/20 px-4 py-1.5 text-xs font-semibold text-white/60 disabled:opacity-30"
          >
            Next →
          </button>
        </div>
      )}

      {!user && (
        <p className="text-center text-sm text-white/40">
          <Link to="/register" className="text-brand-glow hover:underline">Create an account</Link>
          {" "}to track your rank.
        </p>
      )}
    </div>
  );
};

export default LeaderboardPage;
